import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { RouterModule } from '@angular/router';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { environment } from '@environments/environment';
import { lfxPreset } from '@linuxfoundation/lfx-ui-core';
import { OpenFeatureModule } from '@openfeature/angular-sdk';
import { LaunchDarklyClientProvider } from '@openfeature/launchdarkly-client-provider';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { basicLogger } from 'launchdarkly-js-client-sdk';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { SkeletonModule } from 'primeng/skeleton';
import { StyleClassModule } from 'primeng/styleclass';
import { ToastModule } from 'primeng/toast';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CommonHeaderComponent } from './shared/components/common-header/common-header.component';
import { ContactSupportComponent } from './shared/components/contact-support/contact-support.component';
import { NavigationComponent } from './shared/components/navigation/navigation.component';
import { GlobalErrorHandler } from './shared/services/global-error-handler.service';
import { MetaService } from './shared/services/meta.service';
import { ProjectService } from './shared/services/project.service';

/**
 * Recursively converts lfxPreset design tokens into plain string values that
 * PrimeNG's definePreset() expects. Required because @linuxfoundation/lfx-ui-core
 * exports tokens as objects — { $value, $type } (DTCG, v0.2.0+) or { value, type }
 * (legacy, v0.0.x) — instead of plain strings. Without this, CSS custom properties
 * resolve to [object Object] and all themed component styling breaks (dialog backdrops,
 * overlay colors, etc.). This can be removed once lfx-ui-core exports plain string tokens.
 * Non-string leaf values (e.g. shadow objects) are dropped — Aura base provides defaults.
 */
function flattenTokens(obj: any): any {
  if (obj == null || typeof obj !== 'object') {
    return obj;
  }
  // DTCG format: { $value: '...', $type: '...' }
  if ('$value' in obj && Object.keys(obj).every((k) => k === '$value' || k === '$type')) {
    return typeof obj.$value === 'string' ? obj.$value : undefined;
  }
  // Legacy format: { value: '...', type: '...' }
  if ('value' in obj && Object.keys(obj).every((k) => k === 'value' || k === 'type')) {
    return typeof obj.value === 'string' ? obj.value : undefined;
  }
  const result: any = {};
  for (const key of Object.keys(obj)) {
    if (key === 'figma') continue;
    const val = flattenTokens(obj[key]);
    if (val !== undefined) {
      result[key] = val;
    }
  }
  return result;
}

const customPreset = definePreset(Aura, {
  primitive: flattenTokens(lfxPreset.primitive),
  semantic: flattenTokens(lfxPreset.semantic),
  components: {
    ...flattenTokens(lfxPreset.component),
    button: {
      ...flattenTokens(lfxPreset.component.button),
      primary: {
        hover: {
          background: 'var(--p-color-primary-400)',
        },
      },
    },
    toggleswitch: {
      ...flattenTokens(lfxPreset.component.toggleswitch),
      handle: {
        ...flattenTokens(lfxPreset.component.toggleswitch?.handle),
        border: {
          radius: '50%',
        },
      },
    },
    table: {},
  },
});

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ReactiveFormsModule,
    ConfirmPopupModule,
    AuthModule.forRoot({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      useCookiesForTransactions: true,
      authorizationParams: {
        redirect_uri: `${environment.auth0.redirectUrl}`,
        audience: environment.auth0.audience,
        scope: 'access:api openid email profile',
      },
      httpInterceptor: {
        allowedList: [
          {
            /* The endpoint you would like to prefix the access token with */
            uri: `${environment.apiURL}/*`,
          },
          {
            uri: `${environment.apiV2URL}/*`,
          },
        ],
      },
    }),
    ToastModule,
    RouterModule.forRoot([]),
    ContactSupportComponent,
    NavigationComponent,
    CommonHeaderComponent,
    SkeletonModule,
    OpenFeatureModule.forRoot({
      provider: new LaunchDarklyClientProvider(environment.launchDarklyClientId, {
        initializationTimeout: 5,
        streaming: true,
        logger: basicLogger({ level: 'none' }),
      }),
    }),
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    MessageService,
    DialogService,
    ConfirmationService,
    DynamicDialogRef,
    StyleClassModule,
    ProjectService,
    MetaService,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: customPreset,
        options: {
          darkModeSelector: '.my-app-dark',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
        },
      },
    }),
  ],
})
export class AppModule {}
